import { FormEvent } from "react";
import { FormControl } from "@mui/material";
import { useTranslate } from "@/hooks";
import { ButtonType } from "@/helpers/parsingChatMessageButtons";
import { CustomButton, CustomTextField, CustomMenuItem, DraggablePopoverComponent } from "@/ui";
import { IButtonFormProps } from "./ButtonForm.props";
import styles from "./ButtonForm.module.scss";

const BUTTONS_TYPES: { type: ButtonType; name: string }[] = [
	{ type: "button", name: "selectItem_sendingText" },
	{ type: "link", name: "selectItem_followLink" },
	{ type: "sendgeo", name: "selectItem_sendingGeolocation" },
	{ type: "sendcontact", name: "selectItem_sendingContactInfo" },
	{ type: "app", name: "selectItem_openWidget" },
];

const ButtonForm = ({
	anchorForButtonForm,
	setAnchorForButtonForm,
	selectButtonType,
	setSelectButtonType,
	inputButtonName,
	setInputButtonName,
	inputButtonData,
	setInputButtonData,
	buttonChangeFlg,
	setButtonChangeFlg,
	setButtonsInPhrase,
	phrase,
	channel,
}: IButtonFormProps): JSX.Element => {
	const translate = useTranslate(); // hook для перевода текста

	// название поля ввода данных
	const getLabelInputButtonData = (selectButtonType: Exclude<ButtonType, "sendgeo" | "sendcontact">): string => {
		let label = "";
		if (selectButtonType === "button") label = "input_textToSend";
		else if (selectButtonType === "link") label = "input_link";
		else {
			if (channel === "telegram" || channel === "max") label = "input_link";
			else if (channel === "vkontakte") label = "input_applicationId";
			else label = "input_applicationIdOrLinl";
		}
		return translate(label);
	};

	// ограничение создания кнопок (специфика reply и inline)
	const isDisabledSelectButtonType = (type: ButtonType): boolean => {
		if (channel === "telegram") {
			if (phrase.includes("sendcontact") || phrase.includes("sendgeo")) {
				if (type === "sendcontact" || type === "sendgeo") return false;
				else return true;
			} else if (phrase.includes("button") || phrase.includes("link") || phrase.includes("app")) {
				if (type === "sendcontact" || type === "sendgeo") return true;
				else return false;
			} else return false;
		} else {
			return channel === "vkontakte" && type === "sendcontact";
		}
	};

	// обработчик вставки/редактирования кнопки во фразе
	const handleButtonInsertion = (e: FormEvent<HTMLFormElement>): void => {
		e.preventDefault();
		let buttonTemplate =
			selectButtonType === "button" || selectButtonType === "link" || selectButtonType === "app"
				? `{${selectButtonType}:${inputButtonName}=${inputButtonData}}`
				: `{${selectButtonType}:${inputButtonName}}`; // шаблон кнопки
		if (buttonChangeFlg.isChange && buttonChangeFlg.originalTemplate.includes("\n")) buttonTemplate += "\n";
		// если изменение
		if (buttonChangeFlg.isChange) {
			setButtonsInPhrase((prev) => [
				...prev.map((buttonBlock) => {
					if (buttonBlock.includes(buttonChangeFlg.originalTemplate)) {
						const buttonCodeIndex = buttonBlock.findIndex((buttonCode) => buttonCode === buttonChangeFlg.originalTemplate);
						if (buttonCodeIndex >= 0) {
							buttonBlock.splice(buttonCodeIndex, 1, buttonTemplate);
							return buttonBlock;
						} else return buttonBlock;
					} else return buttonBlock;
				}),
			]); // изменяем кнопку в state
		} else {
			setButtonsInPhrase((prev) => {
				if (prev.length > 0) prev[prev.length - 1].push(buttonTemplate);
				else prev.push([buttonTemplate]);
				return [...prev];
			}); // добавляем кнопку в state
		}
		handleFormClosing();
	};

	// обработчик удаления кнопки из фразы
	const handleButtonDeletion = (): void => {
		setAnchorForButtonForm(null); // закрываем форму
		setButtonsInPhrase((prev) => [
			...prev.map((buttonBlock) => {
				return buttonBlock.filter((buttonCode) => buttonCode !== buttonChangeFlg.originalTemplate);
			}),
		]); // удаляем кнопку из state
		handleFormClosing();
	};

	// обработчик закрытия формы
	const handleFormClosing = (): void => {
		setAnchorForButtonForm(null);
		setInputButtonName("");
		setInputButtonData("");
		setButtonChangeFlg({ isChange: false, originalTemplate: "" }); // сброс флага изменения
	};

	return (
		<DraggablePopoverComponent
			anchorEl={anchorForButtonForm}
			setAnchorEl={setAnchorForButtonForm}
			header="title_button"
			closeHandler={handleFormClosing}
			disableDefaultCloseButton
			sx={{ width: "500px" }}
		>
			<form onSubmit={handleButtonInsertion}>
				{/* тип кнопки */}
				<CustomTextField fullWidth margin="dense" select value={selectButtonType} onChange={(e) => setSelectButtonType(e.target.value as ButtonType)}>
					{BUTTONS_TYPES.map(({ type, name }) => (
						<CustomMenuItem key={type} value={type} disabled={isDisabledSelectButtonType(type)}>
							{translate(name)}
						</CustomMenuItem>
					))}
				</CustomTextField>

				{/* название */}
				<CustomTextField
					fullWidth
					margin="dense"
					autoFocus
					required
					label={translate("input_name")}
					value={inputButtonName}
					onChange={(e) => setInputButtonName(e.target.value.replace(/}|=/g, ""))}
				/>
				{/* данные кнопки */}
				{(selectButtonType === "button" || selectButtonType === "link" || selectButtonType === "app") && (
					<CustomTextField
						fullWidth
						margin="dense"
						required
						label={getLabelInputButtonData(selectButtonType)}
						value={inputButtonData}
						onChange={(e) => setInputButtonData(e.target.value.replace(/}/g, ""))}
					/>
				)}

				<div className={styles.block}>
					{buttonChangeFlg.isChange ? (
						<>
							<FormControl fullWidth margin="dense">
								<CustomButton type="submit">{translate("button_save")}</CustomButton>
							</FormControl>
							<FormControl fullWidth margin="dense">
								<CustomButton type="button" color="error" onClick={handleButtonDeletion}>
									{translate("button_delete")}
								</CustomButton>
							</FormControl>
						</>
					) : (
						<FormControl fullWidth margin="dense">
							<CustomButton type="submit">{translate("button_add")}</CustomButton>
						</FormControl>
					)}
					{/* закрыть */}
					<FormControl fullWidth margin="dense">
						<CustomButton onClick={handleFormClosing} type="button">
							{translate("button_close")}
						</CustomButton>
					</FormControl>
				</div>
			</form>
		</DraggablePopoverComponent>
	);
};

export default ButtonForm;
